import {useAppDispatch, useFeatureToggle, useModal} from '@hooks';
import {
  actions,
  getAllConnectedUsers,
  getGroupWithWorkdayCountByDate,
  getIsLoadingConnections,
  useAppSelector,
} from '@lib/store';
import {FlexCol, FlexRow, H3, H4} from '@quarks';
import {cn} from '@utils';
import {Fragment, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useConnectionsSchedule} from '@lib/store/src/ui/hooks/timeslots/useConnectionsSchedule';
import {NoConnections, NoOneThere} from './EmptyState';
import {ConnectionsLoader} from './ConnectionsLoader';
import {ModalPage} from '../../ModalPage';
import {
  getGroupAndReservationData,
  getGroupWorkspaceReservations,
  isLoadingGroupWorkspaceReservations,
} from '@lib/store/src/data/groups';
import {ConnectionOverviewGroupSection} from './ConnectionOverviewGroupSection';
import {ConnectionOverviewOccupantsSection} from './ConnectionOverviewOccupantsSection';
import {constructConnectionsOverview, getOccupantsFromSection} from '@lib/store/src/data/timeslots/viewmodel-helpers';

export const ConnectionOverview = ({
  dateKey,
  asBigPage,
  hideTitle,
  buildingId,
  groupId,
}: {
  dateKey: string;
  asBigPage?: boolean;
  hideTitle?: boolean;
  buildingId?: string;
  groupId?: string;
}) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();

  const {addPages, openModal, closeModal} = useModal();
  const enabledFeatures = useFeatureToggle();

  const groupsWithWorkdayCount = useAppSelector((state) => getGroupWithWorkdayCountByDate(state, dateKey));
  const groupWorkspaceReservations = useAppSelector(getGroupWorkspaceReservations);

  const groupIds = useMemo(() => groupsWithWorkdayCount.map((gr) => gr.id), [groupsWithWorkdayCount]);

  const isLoadingGroupReservations = useAppSelector((state) =>
    isLoadingGroupWorkspaceReservations(state, {groupIds, dateKey}),
  );
  const groupAndReservationData = useAppSelector((state) => getGroupAndReservationData(state, dateKey));

  useEffect(() => {
    if (!enabledFeatures.Groups || !enabledFeatures.TimeslotsUI) return;
    groupIds.forEach((gId) => {
      const range = {startDate: `${dateKey}T00:00`, endDate: `${dateKey}T23:59`};
      const key: `${string}:${string}:${string}` = `${gId}:${range.startDate}:${range.endDate}`;

      const entry = groupWorkspaceReservations[key];
      if (entry && (entry.status === 'pending' || entry.status === 'fulfilled')) return;

      dispatch(
        actions.loadGroupWorkspaceReservations({
          groupId: gId,
          range,
        }),
      );
    });
  }, [dateKey, dispatch, enabledFeatures.Groups, enabledFeatures.TimeslotsUI, groupIds, groupWorkspaceReservations]);

  const connectionsSchedule = useConnectionsSchedule(dateKey);
  const connectionsLoading = useAppSelector(getIsLoadingConnections);
  const connectedUsers = useAppSelector(getAllConnectedUsers);
  const overview = constructConnectionsOverview({
    connectedUsers,
    connectedGroups: groupAndReservationData,
    connectionWorkdays: connectionsSchedule.workdays,
    connectionWorkspaceReservations: connectionsSchedule.workspaceResevations,
  });

  const connectionsForBuilding: typeof overview = overview.filter((o) => {
    const occupants = getOccupantsFromSection(o);
    return !buildingId || occupants?.[0]?.reservations[0]?.workspace.buildingId === buildingId;
  });

  if (connectionsLoading || connectionsSchedule.status === 'pending' || isLoadingGroupReservations)
    return <ConnectionsLoader />;

  return (
    <FlexCol
      gap={16}
      className="w-full">
      {!hideTitle && (
        <FlexRow>
          <H3 className="pl-0">{t('screen:AllConnections')}</H3>
          {overview.length > 1 && (
            <button
              className="text-collaborative-blue-500* font-semibold text-sm"
              onClick={() => {
                addPages([
                  <ModalPage
                    title="Connections at the office"
                    onClose={closeModal}>
                    <div className="py-4">
                      <ConnectionOverview
                        hideTitle
                        dateKey={dateKey}
                      />
                    </div>
                  </ModalPage>,
                ]);
                openModal();
              }}>
              {t('ViewAll')}
            </button>
          )}
        </FlexRow>
      )}

      {connectedUsers.length === 0 && <NoConnections />}
      {connectionsForBuilding.length === 0 && <NoOneThere />}

      {connectionsForBuilding.map((locationSection, i) => {
        return (
          <Fragment key={locationSection.locationName + i}>
            {!buildingId && <H4 className="pl-0">{locationSection.locationName}</H4>}
            <div className={cn('grid full-w grid-cols-1 gap-y-8')}>
              {!groupId && locationSection.groups.length > 0 && (
                <ConnectionOverviewGroupSection
                  locationSection={locationSection}
                  dateKey={dateKey}
                />
              )}

              <ConnectionOverviewOccupantsSection
                groupId={groupId}
                locationSection={locationSection}
                dateKey={dateKey}
                asBigPage={asBigPage}
              />
            </div>
          </Fragment>
        );
      })}
    </FlexCol>
  );
};
